
/**
 * Dynamic Practice Page Component
 * 
 * This component wraps PracticeContentLayout but fetches content from the API
 * instead of using hardcoded content. It maintains the same 3-column layout
 * (left sidebar, middle content, right sidebar) but uses DynamicContentPage
 * for the content area.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PracticeContentLayout, { TopicItem } from './PracticeContentLayout';
import DynamicContentPage from '../content/DynamicContentPage';
import QuizComponent from './QuizComponent';
import { getSlugFromTopicId } from '../../utils/topicIdToSlugMap';
import { contentService } from '../../services/contentService';
import { Loader2, AlertCircle } from 'lucide-react';
import { useSectionProgress } from '../../hooks/useSectionProgress';
import { ContentPage } from '../../types/content';

interface DynamicPracticePageProps {
  topicType: 'nodejs' | 'docker' | 'databases' | 'system-design' | 'lld' | 'hld';
  topics: TopicItem[];
  title: string;
  basePath?: string;
  getSlugForTopic?: (topicId: string) => string | null;
  staticContentPages?: Record<string, ContentPage>;
  articleClassName?: string;
}

const DynamicPracticePage: React.FC<DynamicPracticePageProps> = ({
  topicType,
  topics,
  title,
  basePath,
  getSlugForTopic,
  staticContentPages,
  articleClassName,
}) => {
  const { topicId } = useParams<{ topicId?: string }>();
  const navigate = useNavigate();
  const { isLessonComplete, markLessonComplete } = useSectionProgress(topicType);
  const [currentSlug, setCurrentSlug] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [availableSlugs, setAvailableSlugs] = useState<Set<string>>(new Set());
  const [isLoadingSlugs, setIsLoadingSlugs] = useState(true);
  const routeBasePath = basePath ?? (
    topicType === 'lld' ? '/system-design/lld'
      : topicType === 'hld' ? '/system-design/hld'
        : topicType === 'system-design' ? '/system-design'
          : `/learn/${topicType}`
  );
  const resolveSlug = useCallback(
    (id: string) => getSlugForTopic ? getSlugForTopic(id) : getSlugFromTopicId(id, topicType),
    [getSlugForTopic, topicType],
  );

  // Find first leaf topic
  const findFirstLeafTopic = useCallback((items: TopicItem[]): string | null => {
    for (const item of items) {
      if (item.children && item.children.length > 0) {
        const childResult = findFirstLeafTopic(item.children);
        if (childResult) return childResult;
      } else {
        return item.id;
      }
    }
    return null;
  }, []);

  // Find topic by ID and check if it has children
  const findTopicById = useCallback((items: TopicItem[], id: string): TopicItem | null => {
    for (const item of items) {
      if (item.id === id) {
        return item;
      }
      if (item.children && item.children.length > 0) {
        const found = findTopicById(item.children, id);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // Get first child topic ID if topic has children
  const getFirstChildTopicId = useCallback((items: TopicItem[], id: string): string | null => {
    const topic = findTopicById(items, id);
    if (topic && topic.children && topic.children.length > 0) {
      // Find first leaf topic within this topic's children
      return findFirstLeafTopic(topic.children);
    }
    return null;
  }, [findTopicById, findFirstLeafTopic]);

  // Calculate total chapters
  const calculateTotalChapters = useCallback((items: TopicItem[]): number => {
    let count = 0;
    for (const item of items) {
      if (!item.children || item.children.length === 0) {
        if (item.trackProgress === false) continue;
        count++;
      } else {
        count += calculateTotalChapters(item.children);
      }
    }
    return count;
  }, []);

  // Fetch available content page slugs on mount
  useEffect(() => {
    const fetchAvailableSlugs = async () => {
      setIsLoadingSlugs(true);
      if (staticContentPages) {
        setAvailableSlugs(new Set(Object.keys(staticContentPages)));
        setIsLoadingSlugs(false);
        return;
      }
      try {
        const tags = topicType === 'lld'
          ? ['lld']
          : topicType === 'hld'
            ? ['hld']
            : topicType === 'system-design'
              ? ['system-design']
              : [topicType];

        // Race the fetch against a 8s timeout to prevent the page from being stuck
        // on "Loading topics..." when the response payload is too large.
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Slug fetch timeout')), 8000)
        );
        const response = await Promise.race([
          contentService.query({ tags, status: 'published', limit: 1000 }),
          timeoutPromise,
        ]);

        // Debug: Log fetched pages
        console.log(`[DynamicPracticePage] Fetched ${response.pages.length} pages for topicType: ${topicType}`);
        console.log(`[DynamicPracticePage] Available slugs:`, response.pages.map(p => p.slug));

        // Create a set of available slugs
        const slugs = new Set(response.pages.map(page => page.slug));
        setAvailableSlugs(slugs);
      } catch (err) {
        console.error('[DynamicPracticePage] Failed to fetch available content pages:', err);
        // If fetch fails, allow all topics (fallback)
        setAvailableSlugs(new Set());
      } finally {
        setIsLoadingSlugs(false);
      }
    };

    fetchAvailableSlugs();
  }, [topicType, staticContentPages]);

  // Filter topics to only include those with active content pages
  const filteredTopics = useMemo(() => {
    if (isLoadingSlugs || availableSlugs.size === 0) {
      return topics; // Return all topics while loading or if no slugs available
    }

    const filterTopics = (items: TopicItem[]): TopicItem[] => {
      return items
        .map(item => {
          if (item.children && item.children.length > 0) {
            // Has children - recursively filter children
            const filteredChildren = filterTopics(item.children);
            if (filteredChildren.length === 0) {
              return null; // Remove parent if no children remain
            }
            return {
              ...item,
              children: filteredChildren,
            };
          } else {
            // Leaf node - check if content page exists and is active
            // Quiz topics and explicit workspace/tool routes are always kept.
            if (item.id.endsWith('-quiz') || item.path) {
              return item;
            }
            const slug = resolveSlug(item.id);
            if (slug && availableSlugs.has(slug)) {
              return item; // Keep topic if content page is available
            }
            return null; // Remove topic if no active content page
          }
        })
        .filter((item): item is TopicItem => item !== null);
    };

    return filterTopics(topics);
  }, [topics, availableSlugs, isLoadingSlugs, resolveSlug]);

  const totalChapters = calculateTotalChapters(filteredTopics);

  // Compute topics with progress-based completed state
  const topicsWithProgress: TopicItem[] = useMemo(() => {
    const addProgress = (items: TopicItem[]): TopicItem[] => {
      return items.map((item) => {
        if (item.children && item.children.length > 0) {
          const updatedChildren = addProgress(item.children);
          const allComplete = updatedChildren.every((c) => c.completed);
          return { ...item, children: updatedChildren, completed: allComplete };
        }
        // A workspace can be required before its parent problem is complete,
        // while still remaining outside the article chapter total.
        return { ...item, completed: isLessonComplete(item.id) };
      });
    };
    return addProgress(filteredTopics);
  }, [filteredTopics, isLessonComplete]);

  const completedChapters = useMemo(() => {
    let count = 0;
    const countCompleted = (items: TopicItem[]) => {
      for (const item of items) {
        if (!item.children || item.children.length === 0) {
          if (item.trackProgress === false) continue;
          if (item.completed) count++;
        } else {
          countCompleted(item.children);
        }
      }
    };
    countCompleted(topicsWithProgress);
    return count;
  }, [topicsWithProgress]);

  // Map topic ID to content page slug
  useEffect(() => {
    if (isLoadingSlugs) return; // Wait for slugs to load

    const currentTopicId = topicId || findFirstLeafTopic(filteredTopics) || '';

    if (!currentTopicId) {
      setError('No topic found');
      return;
    }

    // Check if current topic is a parent topic (has children)
    const firstChildId = getFirstChildTopicId(filteredTopics, currentTopicId);
    if (firstChildId) {
      // This is a parent topic - redirect to first child
      navigate(`${routeBasePath}/${firstChildId}`, { replace: true });
      return;
    }

    // Quiz topics don't need a content page slug
    if (currentTopicId.endsWith('-quiz')) {
      setCurrentSlug(null);
      setError(null);
      return;
    }

    const slug = resolveSlug(currentTopicId);

    if (!slug) {
      setError(`No content page found for topic: ${currentTopicId}`);
      return;
    }

    // Debug: Log slug lookup
    console.log(`[DynamicPracticePage] Topic ID: ${currentTopicId} → Slug: ${slug}`);
    console.log(`[DynamicPracticePage] Available slugs count: ${availableSlugs.size}`);
    console.log(`[DynamicPracticePage] Slug in available slugs: ${availableSlugs.has(slug)}`);

    // Verify slug is in available slugs
    if (!availableSlugs.has(slug)) {
      console.warn(`[DynamicPracticePage] Content page "${slug}" not found in available slugs. Available slugs:`, Array.from(availableSlugs));
      setError(`Content page "${slug}" is not available. Make sure the content has been seeded and is active.`);
      return;
    }

    setCurrentSlug(slug);
    setError(null);
  }, [topicId, filteredTopics, availableSlugs, isLoadingSlugs, findFirstLeafTopic, getFirstChildTopicId, navigate, resolveSlug, routeBasePath]);

  // Scroll to top when slug changes (new content page loaded)
  useEffect(() => {
    if (currentSlug) {
      // Immediate scroll first
      const contentArea = document.querySelector('[data-content-area]') as HTMLElement;
      if (contentArea) {
        contentArea.scrollTo({ top: 0, behavior: 'auto' });
      }
      window.scrollTo({ top: 0, behavior: 'auto' });

      // Small delay to ensure content is rendered, then smooth scroll
      setTimeout(() => {
        const contentArea = document.querySelector('[data-content-area]') as HTMLElement;
        if (contentArea) {
          contentArea.scrollTo({ top: 0, behavior: 'smooth' });
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 150);
    }
  }, [currentSlug]);

  // Redirect to first topic if no topicId provided
  useEffect(() => {
    if (isLoadingSlugs) return; // Wait for slugs to load

    if (!topicId) {
      const firstTopic = findFirstLeafTopic(filteredTopics);
      if (firstTopic) {
        // System design, LLD, and HLD use /system-design route, others use /learn
        navigate(`${routeBasePath}/${firstTopic}`, { replace: true });
      }
    }
  }, [topicId, filteredTopics, navigate, isLoadingSlugs, findFirstLeafTopic, routeBasePath]);

  const handleTopicSelect = useCallback((selectedTopicId: string, path?: string) => {
    navigate(path ?? `${routeBasePath}/${selectedTopicId}`);
  }, [navigate, routeBasePath]);

  const currentTopicId = topicId || findFirstLeafTopic(filteredTopics) || '';

  if (isLoadingSlugs) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 dark:text-primary-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading topics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Content Not Found
          </h2>
          <p className="text-gray-600 dark:text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!currentSlug && !currentTopicId.endsWith('-quiz')) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 dark:text-primary-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading content...</p>
        </div>
      </div>
    );
  }

  // Check if current topic is a quiz
  const isQuizTopic = currentTopicId.endsWith('-quiz');
  const lessonCompleted = isLessonComplete(currentTopicId);

  const markCompleteButton = (
    <button
      onClick={() => {
        if (lessonCompleted) {
          markLessonComplete(`__UNCOMPLETE__${currentTopicId}`);
        } else {
          markLessonComplete(currentTopicId);
        }
      }}
      className={`article-completion-button ${lessonCompleted ? 'is-complete' : 'is-incomplete'} inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${lessonCompleted
        ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-900/50'
        : 'bg-primary-600 text-white hover:bg-primary-700'
        }`}
    >
      {lessonCompleted ? (
        <>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Completed ✓
        </>
      ) : (
        <>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Mark as Complete
        </>
      )}
    </button>
  );

  return (
    <PracticeContentLayout
      className={articleClassName ?? (topicType === 'system-design' ? 'system-design-article-page' : '')}
      title={title}
      totalChapters={totalChapters}
      completedChapters={completedChapters}
      topics={topicsWithProgress}
      currentTopicId={currentTopicId}
      onTopicSelect={handleTopicSelect}
      onToggleComplete={(topicId, completed) => {
        if (completed) {
          markLessonComplete(topicId);
        } else {
          markLessonComplete(`__UNCOMPLETE__${topicId}`);
        }
      }}
      content={
        isQuizTopic ? (
          <QuizComponent quizSlug={currentTopicId} />
        ) : (
          <DynamicContentPage
            slug={currentSlug!}
            pageOverride={staticContentPages?.[currentSlug!]}
            showAuthorInfo={true}
            showRating={true}
            showComments={true}
            noWrapper={true}
            className=""
            markCompleteButton={markCompleteButton}
          />
        )
      }
    />
  );
};

export default DynamicPracticePage;
